import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import ScheduleEditor from 'mui-cron';
import React, { useId } from 'react';

import { SelectMenu } from '../../../../../components/atoms/SelectMenu';
import { SCHEDULE_TYPE } from '../../../../../constants/report';
import { FIELD_HINT_SX, SECTION_HEADING_SX } from '../ReportEditFragment/reportEditDialog.styles';

/** `CLEAR` shouts; this is a "start over" affordance, not a destructive one. */
const CRON_LOCALE = { clearButtonText: 'Reset' };

/**
 * Themes the third-party `mui-cron` editor from the outside.
 *
 * Its shipped stylesheet hardcodes `font-family: 'roboto'` on `.react-js-cron` and
 * on every field inside it, which is why the row rendered in a different face to
 * the rest of the app (Inter). It also hardcodes 5px/10px margins and leaves the
 * selects at MUI's default 14px.
 *
 * Emotion emits these as `.css-hash .react-js-cron…` — specificity (0,2,0) or
 * better against the package's bare (0,1,0) class rules — so they win without a
 * single `!important`, and the package itself is neither forked nor patched.
 */
const CRON_EDITOR_SX: SxProps<Theme> = {
  // `rowGap` replaces the per-field bottom margin the package uses to space
  // wrapped rows, so the last row does not carry a trailing 10px.
  '& .react-js-cron': { fontFamily: 'inherit', rowGap: 1 },
  '& .react-js-cron-field': { fontFamily: 'inherit', mb: 0 },
  // The connector words — Every / on / and / at — between the selects.
  '& .react-js-cron-field > span': { fontSize: '13px', color: 'v4.textMuted', ml: 0.75 },
  '& div.react-js-cron-select': { ml: 0.75 },
  '& .react-js-cron-select': {
    fontSize: '13px',
    bgcolor: 'v4.surface',
    // An unset field renders no tag at all, so the box needs a floor width or it
    // collapses to a sliver and stops reading as something you can click.
    '& .MuiSelect-select': { py: 0.75, minWidth: 40 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'v4.border' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'v4.borderDark' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'v4.accentDark',
      borderWidth: 1,
    },
  },
  // The package hardcodes `variant="contained" color="secondary"` on this button
  // and never spreads `clearButtonProps`, so restyling it has to happen here.
  '& .react-js-cron-clear-button': {
    mb: 0,
    px: 1,
    py: 0.25,
    minWidth: 0,
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    color: 'v4.accentDark',
    bgcolor: 'transparent',
    boxShadow: 'none',
    '&:hover': { bgcolor: 'v4.tableHover', boxShadow: 'none' },
  },
};

/** Groups the cron controls as one field, matching the status row on tab 2. */
const CUSTOM_PANEL_SX: SxProps<Theme> = {
  p: 1.5,
  border: '1px solid',
  borderColor: 'v4.border',
  borderRadius: 1,
  bgcolor: 'v4.tableEven',
};

const CUSTOM_HINT_SX: SxProps<Theme> = { color: 'v4.textMuted', mt: 1, display: 'block' };

export interface ScheduleEmailScheduleFieldsProps {
  scheduleType: string;
  onChangeScheduleType: (type: string) => void;
  scheduleValue: string;
  onChangeScheduleValue: (cron: string) => void;
  /** Only `Custom` gets the cron editor; the presets describe themselves. */
  isCustomSchedule: boolean;
}

export const ScheduleEmailScheduleFields: React.FC<ScheduleEmailScheduleFieldsProps> = ({
  scheduleType,
  onChangeScheduleType,
  scheduleValue,
  onChangeScheduleValue,
  isCustomSchedule,
}) => {
  const hintId = useId();

  return (
    <Box component="section">
      <Typography sx={SECTION_HEADING_SX}>Schedule</Typography>
      <Stack spacing={1.75} sx={{ mt: 1 }}>
        <Box>
          <SelectMenu
            id="email-schedule-type-"
            label="Schedule *"
            required
            items={SCHEDULE_TYPE}
            selectedValue={scheduleType}
            onChange={onChangeScheduleType}
            optionalLabelEnabled
            optionalLabel="Select Schedule"
          />
          <Typography variant="caption" sx={FIELD_HINT_SX}>
            All times are in the Melbourne timezone.
          </Typography>
        </Box>

        {/* Presets used to render this editor greyed out, restating the option
            already named in the dropdown above. It only appears for Custom now. */}
        {isCustomSchedule && (
          <Box
            sx={CUSTOM_PANEL_SX}
            role="group"
            aria-label="Custom schedule"
            aria-describedby={hintId}
          >
            <Box sx={CRON_EDITOR_SX}>
              <ScheduleEditor
                clockFormat="12-hour-clock"
                leadingZero={['minutes']}
                locale={CRON_LOCALE}
                value={scheduleValue}
                setValue={onChangeScheduleValue}
              />
            </Box>
            <Typography id={hintId} variant="caption" sx={CUSTOM_HINT_SX}>
              An empty box means every: leave the month box empty to send in every month.
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
